import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Paper, Grid, Typography, Button, FormControl, Select, MenuItem, Tooltip } from '@material-ui/core';
import { PlatformSchemaVersionHistory } from './VersionHistory';
import Breadcrumb from '../../atoms/breadcrumb';
import { handleSecSnackBar, invokeGetService, invokePostService, updateBlueprintSlugDetails } from '../../../store/global/actions';
import { PlatformSchemaVersions } from './PlatformSchemaVersions';
import { getBlueprintDetails, getReRender } from '../../../store/global/selectors';
import { PlatformSchemaMigrationWarnings } from './PlatformSchemaMigrationWarnings';
import { useMemo } from 'react';
import { getLoggedInUserId } from '../../../store/auth/selectors';
import { getDetailsFromUrl } from '../../../services/blueprint';
import BlueprintValidationsList from '../blueprintValidator/blueprintValidationsList';


const useStyles = makeStyles((theme) => ({
    container: {
        margin: '90px 0px 0px',
        padding: '0px 15px',
        width: '100%',
    },
    btnContainer: {
        display: "flex",
        justifyContent: "flex-end",
        gap: 8
    },
    btn: {
        textTransform: "none"
    },
    filterContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center'
    },
    select: {
        minWidth: 220,
        background: '#fff'
    }

}));

const PlatformSchema = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { blueprintId } = getDetailsFromUrl();


    const { platformSchemaVersion, user_id: blueprintOwner } = useSelector(getBlueprintDetails) || {};
    const loggedInUserId = useSelector(getLoggedInUserId);
    const reRender = useSelector(getReRender);

    const [activeView, setActiveView] = useState('history');
    const [isLatestAlertVisible, setIsLatestAlertVisible] = useState(false);
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [isMigrating, setIsMigrating] = useState(false);
    const [compatibilityErrors, setCompatibilityErrors] = useState([]);
    const [latestSchemaDesc, setLatestSchemaDesc] = useState("");
    const [isFetching, setIsFetching] = useState(false);
    const [isPlatformSchemaCompatibilityView, setIsPlatformSchemaCompatibilityView] = useState(false);
    const [blueprintValidatorOpen, setBlueprintValidatorOpen] = useState(false);
    const [validationsList, setValidationsList] = useState([]);
    const [validationLoading, setValidationsLoading] = useState(false);

    useEffect(() => {
        isBlueprintUsingLatestPlatformSchema();
    }, [reRender]);


    const isBlueprintOwner = useMemo(() => {
        return Boolean(loggedInUserId === blueprintOwner);
    }, [blueprintOwner, loggedInUserId]);

    const checkPlatformSchemaMigration = (version) => {
        setIsFetching(true);
        setIsPlatformSchemaCompatibilityView(true);
        dispatch(invokeGetService('checkPlatformSchemaMigration', { type: 'manual', selectedVersion: version })).then((response) => {
            if (response?.status === true && response.data) {
                if (response.data.compatibilityErrors) {
                    setCompatibilityErrors(response.data.compatibilityErrors || []);
                }
            }
            setIsFetching(false);
        });
    }

    const fetchValidations = async () => {
        try {
          dispatch(invokeGetService('validateBlueprint', [])).then(response => {
            if (response.status) {
              if (response.data) {
                const errors = response.data?.errors || [];
                const warnings = response.data?.warnings || [];
                const incompatibilities = [...errors, ...warnings];
                if (incompatibilities.length > 0) {
                    setValidationsList([...errors, ...warnings]);
                    setBlueprintValidatorOpen(true);    
                }
              } else {
                console.log("--No validation data found--")
              }
            } else {
              throw new Error('Response not found')
            }
    
          }).catch(error => {
            console.error(`Fetching validations failed. ${error}`);
          })

        } catch (err) {
          console.error("Fetching validations failed.", err);
        } finally {
          
        }
  };



    const migratePlatformSchema = (version) => {
        setIsMigrating(true);
        dispatch(invokePostService('migratePlatformSchema', { platformSchemaVersion: version })).then(async (response) => {
            if (response?.status === true) {
                fetchValidations();
                dispatch(handleSecSnackBar({ open: true, message: response.message, severity: "success" }));
                setIsLatestAlertVisible(false);
                dispatch(updateBlueprintSlugDetails({ platformSchemaVersion: version }));
                localStorage.removeItem(`platformSchemaLatestSeen_${blueprintId}`);
                isBlueprintUsingLatestPlatformSchema();
            }
            setIsMigrating(false);
        });
    }

    const isBlueprintUsingLatestPlatformSchema = () => {
        dispatch(invokeGetService('checkBlueprintOnLatestSchema', [])).then((response) => {
            if (response?.status === true && response.data) {
                if (response.data.isLatestPlatformSchema === false && response.data.latestPlatformSchemaVersion) {
                    setIsLatestAlertVisible(true);
                    setSelectedVersion(response.data.latestPlatformSchemaVersion);
                    setLatestSchemaDesc(response.data.description || "");
                    localStorage.setItem(`platformSchemaLatestSeen_${blueprintId}`, response.data.latestPlatformSchemaVersion);
                } else {
                    localStorage.removeItem(`platformSchemaLatestSeen_${blueprintId}`);
                }
            }
        });
    }



    const onCloseMigrationWarningTable = () => {
        setIsPlatformSchemaCompatibilityView(false);
        setCompatibilityErrors([]);
    };



    return (
        <Grid container className={classes.container} spacing={1} id={"platform_schema_screen"}>
            <Grid item xs={12}>
                <Breadcrumb items={[{ label: 'Hyperblox Runtime Version' }]} isHeading={true} />
            </Grid>
            {!isPlatformSchemaCompatibilityView && <>
                {isLatestAlertVisible && isBlueprintOwner &&
                    <Grid item xs={12}>
                        <Paper
                            elevation={0}
                            style={{
                                background: "#bfd9ff",
                                border: "1px solid #cfd8e3",
                                padding: "12px 16px",
                            }}
                            id="latest_version_alert"
                        >
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                        {`New runtime version released (v ${selectedVersion})`}
                                    </Typography>
                                    {(latestSchemaDesc && latestSchemaDesc !== "") &&
                                        <Typography
                                            variant="body2"
                                            component="div"
                                            dangerouslySetInnerHTML={{ __html: latestSchemaDesc }}
                                        />
                                    }
                                    {(!latestSchemaDesc || latestSchemaDesc === "") &&
                                        <Typography variant="body2">
                                            No details available
                                        </Typography>
                                    }
                                </Grid>

                                <Grid item xs={12} className={classes.btnContainer}>
                                    <Tooltip title="View migration changes" arrow>
                                        <span>
                                            <Button id="show_impact_btn" data-ui-tour-id="platformschema-impact-btn" disabled={isMigrating || !isBlueprintOwner} variant="outlined" className={classes.btn} onClick={() => checkPlatformSchemaMigration(selectedVersion)}>
                                                Show me the impact
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Start migration" arrow>
                                        <span>
                                            <Button id="migrate_btn" data-ui-tour-id="platformschema-migrate-btn" disabled={isMigrating || !isBlueprintOwner} color="primary" variant="contained" className={classes.btn} onClick={() => migratePlatformSchema(selectedVersion)}>
                                                {isMigrating ? 'Migrating..' : 'Migrate the application'}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                }

                <Grid item xs={12} className={classes.filterContainer} id="platform_schema_filter_container">
                    <FormControl variant="outlined" size="small">
                        <Select
                            id="platform_schema_filter"
                            data-ui-tour-id="platformschema-filter-btn"
                            value={activeView}
                            onChange={(e) => setActiveView(e.target.value)}
                            className={classes.select}
                        >
                            <MenuItem value="history">
                                Platform Schema Version History
                            </MenuItem>
                            <MenuItem value="versions">
                                All Platform Schema Versions
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <Typography
                        variant="subtitle1"
                    >
                        {activeView === 'history' ? "Platform Schema Migration History" : "Switch Platform Schema"}
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    {activeView === 'history' && <PlatformSchemaVersionHistory isMigrating={isMigrating} />}
                    {activeView === 'versions' && <PlatformSchemaVersions isMigrating={isMigrating} isBlueprintOwner={isBlueprintOwner} setSelectedVersion={setSelectedVersion} checkPlatformSchemaMigration={checkPlatformSchemaMigration} migratePlatformSchema={migratePlatformSchema} />}
                </Grid>
            </>}

            {isPlatformSchemaCompatibilityView &&
                <PlatformSchemaMigrationWarnings compatibilityErrors={compatibilityErrors} onClose={onCloseMigrationWarningTable} isFetching={isFetching} />
            }

            {blueprintValidatorOpen &&
                <BlueprintValidationsList
                    handleRefresh={fetchValidations}
                    loading={validationLoading}
                    setLoading={setValidationsLoading}
                    validations={validationsList}
                    open={blueprintValidatorOpen}
                    onClose={() => setBlueprintValidatorOpen(false)}
                />
            }

        </Grid>
    );
};

export default PlatformSchema;
